












































import { Component, Mixins, Watch } from "vue-property-decorator";
import { DomainRegistry } from "../../domain";
import { IDocumentImageSource } from "../../domain/document";
import { Rectangle, Point, Size, Color, LineStyle, LineKinds } from "../../domain/drawing";
import { OptionRadioSelector } from "../system/selectors";
import { StandardOption } from "../system/selectors/mixins";
import { IImageErasable } from "./IImageErasable";
import { default as DrawableCanvas } from "./DrawableCanvas.vue";

@Component({
  components: {
    DrawableCanvas,
    OptionRadioSelector,
  },
})
export default class ImageEraser extends Mixins() implements IImageErasable {
  $refs!: {
    canvas: DrawableCanvas,
  };

  data() {
    return {
      rectangle: new Rectangle(new Point(0, 0), new Size(0, 0)),
      scale: 1,
      source: "",
      hiddenElement: null as HTMLElement,
      result: null as IDocumentImageSource,
      isEditing: false,
      strokeSize: 5,
      disabledUndo: true,
      disabledRedo: true,
      isLoading: false,
      isUploading: false,
      handlers: [] as ((isErasing: boolean) => void)[],
    };
  }
  rectangle: Rectangle;
  scale: number;
  source: string;
  hiddenElement: HTMLElement;
  result: IDocumentImageSource;
  isEditing: boolean;
  strokeSize: number;
  disabledUndo: boolean;
  disabledRedo: boolean;
  isLoading: boolean;
  isUploading: boolean;
  handlers: ((isErasing: boolean) => void)[];

  get scaledRectangle() {
    return this.rectangle.multiply(this.scale);
  }

  get canvasStyle() {
    return {
      transform: "scale(" + this.scale + ", " + this.scale + ")",
      transformOrigin: "left top",
      left: this.rectangle.x + "px",
      top: this.rectangle.y + "px",
    };
  }

  get canvasStackMaxSize() {
    return this.$configuration.behavior.document.editor.eraser.stackMaxSize;
  }

  get canvasWidth() {
    return this.rectangle.width;
  }

  get canvasHeight() {
    return this.rectangle.height;
  }

  get canvasLineStyle() {
    return new LineStyle(new Color("#000000"), LineKinds.Normal, this.strokeSize);
  }

  get untouchableTopStyle() {
    return {
      left: 0,
      top: 0,
      width: "100vw",
      height: Math.max(this.rectangle.y, 0) + "px",
    };
  }

  get untouchableBottomStyle() {
    const top = this.rectangle.y + this.scaledRectangle.height;
    return {
      left: 0,
      top: top + "px",
      width: "100vw",
      height: "calc(100vh - " + top + "px)",
    };
  }

  get untouchableLeftStyle() {
    return {
      left: 0,
      top: this.rectangle.y + "px",
      width: Math.max(this.rectangle.x, 0) + "px",
      height: this.scaledRectangle.height + "px",
    };
  }

  get untouchableRightStyle() {
    const left = this.rectangle.x + this.scaledRectangle.width;
    return {
      left: left + "px",
      top: this.rectangle.y + "px",
      width: "calc(100vw - " + left + "px)",
      height: this.scaledRectangle.height + "px",
    };
  }


  get menuStyle() {
    return {
      left: this.rectangle.x + "px",
      top: Math.max(this.rectangle.y - 35, 0) + "px",
    };
  }

  get isShowStrokeSizes(): boolean {
    return this.strokeSizes.length > 1;
  }

  get strokeSizes(): StandardOption<number>[] {
    return this.$configuration.behavior.document.editor.eraser.strokeSizes.map(v => ImageEraser.toOption(v.label, v.size));
  }

  private static toOption(label: string, size: number): StandardOption<number> {
    return new StandardOption<number>(
      label,
      size,
      size + "pxで描画します。"
    );
  }

  get disabledApply(): boolean {
    return this.isLoading || this.isUploading;
  }

  mounted(): void {
    this.strokeSize = this.$configuration.behavior.document.editor.eraser.defaultStrokeSize;
  }

  registerErasingChangedHandler(handler: (isErasing: boolean) => void): void {
    this.handlers.push(handler);
  }

  @Watch("isEditing")
  onIsEditingChanged(newValue: boolean): void {
    for (const handler of this.handlers) {
      handler(newValue);
    }
  }

  async erase(rectangle: Rectangle, scale: number, source: string, hiddenElement: HTMLElement): Promise<IDocumentImageSource> {
    this.rectangle = rectangle;
    this.scale = scale;
    this.source = source;
    this.hiddenElement = hiddenElement;
    this.result = null;
    this.isEditing = true;
    this.isLoading = true;
    await this.$wait(() => !this.isEditing);
    return this.result;
  }

  async apply(): Promise<void> {
    this.isUploading = true;
    const data = this.$refs.canvas.toImageText();
    const repository = DomainRegistry.document.getDocumentRepository();
    const result = await repository.createImageSource(data);
    this.isUploading = false;
    this.finish(result);
  }

  cancel(): void {
    this.finish(null);
  }

  private finish(result: IDocumentImageSource): void {
    this.hiddenElement.style.removeProperty("visibility");
    this.result = result;
    this.isEditing = false;
  }

  undo(): void {
    this.$refs.canvas.undo();
  }

  redo(): void {
    this.$refs.canvas.redo();
  }

  loaded(): void {
    this.isLoading = false;
    this.hiddenElement.style.setProperty("visibility", "hidden");
  }

  stackUpdated(undo: number, redo: number): void {
    this.disabledUndo = (undo === 0);
    this.disabledRedo = (redo === 0);
  }
}
